/**
 * Отметка «человек в сети» для умного распределения переписок.
 *
 * Договор: _vendor/chatwoot/_giper/UNIA_PRESENCE.md.
 *   POST {CHATWOOT_URL}/unia_presence
 *   • телефон: Authorization: Bearer <пропуск UNIA>;
 *   • браузер: пропуск ТЕЛОМ, Content-Type: text/plain, без своих заголовков —
 *     иначе браузер делает предварительный запрос, а Переписки его для чужого
 *     адреса не пропускают.
 *   → 204 отмечен (или человека нет в Переписках), 401 пропуск не принят.
 * Слать раз в 60 с, пока приложение на экране, и сразу при возвращении.
 * Отметка живёт 150 с, закрыли приложение — через 150 с «не в сети».
 */
import config from './config';
import { getFreshAccessToken } from './authToken';
import { isNative } from './platform';

export const PRESENCE_INTERVAL_MS = 60_000;

/** Одна отметка. Никогда не бросает: сбой отметки не должен мешать работе. */
export async function sendPresence(): Promise<void> {
  try {
    const token = await getFreshAccessToken();
    if (!token) return;
    const url = `${config.CHATWOOT_URL}/unia_presence`;
    if (isNative()) {
      await fetch(url, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
    } else {
      await fetch(url, { method: 'POST', headers: { 'Content-Type': 'text/plain' }, body: token });
    }
  } catch { /* сеть мигнула — следующая отметка через минуту */ }
}

export interface PresenceScheduler {
  /** Приложение на экране: отметиться сразу и дальше раз в интервал. */
  resume: () => void;
  /** Приложение ушло в фон: отметки прекращаются. */
  pause: () => void;
  isRunning: () => boolean;
}

/**
 * Планировщик отметок. resume/pause идемпотентны: при возврате на экран
 * приходят сразу несколько событий (смена состояния приложения, видимость
 * страницы), а отметка должна уйти один раз.
 */
export function createPresenceScheduler(
  send: () => Promise<void> = sendPresence,
  intervalMs: number = PRESENCE_INTERVAL_MS,
): PresenceScheduler {
  let timer: ReturnType<typeof setInterval> | null = null;
  const fire = () => { void send().catch(() => { /* не бросаем */ }); };
  return {
    resume() {
      if (timer !== null) return;
      fire();
      timer = setInterval(fire, intervalMs);
    },
    pause() {
      if (timer === null) return;
      clearInterval(timer);
      timer = null;
    },
    isRunning: () => timer !== null,
  };
}

/** Подключить планировщик к жизни приложения. Возвращает функцию отключения. */
export function startPresence(): () => void {
  const sch = createPresenceScheduler();
  const onVisibility = () => (document.visibilityState === 'visible' ? sch.resume() : sch.pause());
  document.addEventListener('visibilitychange', onVisibility);

  const снять: Array<() => void> = [];
  let отключено = false;
  if (isNative()) {
    void import('@capacitor/app').then(({ App }) => {
      void App.addListener('appStateChange', ({ isActive }) => (isActive ? sch.resume() : sch.pause()))
        .then((h) => { if (отключено) void h.remove(); else снять.push(() => void h.remove()); });
      void App.addListener('resume', () => sch.resume())
        .then((h) => { if (отключено) void h.remove(); else снять.push(() => void h.remove()); });
      void App.addListener('pause', () => sch.pause())
        .then((h) => { if (отключено) void h.remove(); else снять.push(() => void h.remove()); });
    }).catch(() => { /* без плагина остаются события страницы */ });
  }

  if (document.visibilityState === 'visible') sch.resume();
  return () => {
    отключено = true;
    document.removeEventListener('visibilitychange', onVisibility);
    снять.forEach((f) => f());
    sch.pause();
  };
}
